<script setup lang="ts">
import { computed, ref } from 'vue'
import IconNotification from '@/components/icons/IconNotification.vue'
import { useI18n } from 'vue-i18n'
import { useNotificationStore } from '@/stores/notification'
import { storeToRefs } from 'pinia'
import utils from '@/utils/utils'
import IconBase from '@/components/base/IconBase.vue'
const { t } = useI18n()
const notificationStore = useNotificationStore()

const { notifications, countUnread } = storeToRefs(notificationStore)

const hasNotification = computed(() => countUnread.value > 0)

const menu = ref(false)

const sortedNotifications = computed(() => {
  return [...notifications.value].sort((a, b) => {
    return new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
  })
})

const getStatusColor = (status: string) => {
  switch (status) {
    case 'success':
      return 'success'
    case 'error':
      return 'error'
    case 'uploading':
      return 'primary'
    default:
      return 'brand-green'
  }
}

const getStatusIcon = (status: string) => {
  switch (status) {
    case 'success':
      return 'mdi-check-circle'
    case 'error':
      return 'mdi-alert-circle'
    case 'uploading':
      return 'mdi-upload'
    default:
      return 'mdi-information'
  }
}

const handleClearAll = () => {
  notificationStore.clear()
}
</script>

<template>
  <v-menu
    v-model="menu"
    min-width="400px"
    width="400px"
    location="bottom end"
    :close-on-content-click="false"
  >
    <template v-slot:activator="{ props }">
      <v-badge
        data-task-belt
        :content="countUnread"
        :model-value="hasNotification"
        color="error"
        overlap
      >
        <IconBase
          v-bind="props"
          variant="subtle"
          :aria-label="t('notifications')"
          :active="menu"
        >
          <template #icon>
            <IconNotification />
          </template>
        </IconBase>
        <v-tooltip activator="parent" location="bottom">{{ t('notifications') }}</v-tooltip>
      </v-badge>
    </template>
    <v-card class="w-100">
      <v-card-title class="d-flex justify-space-between align-center">
        <span>{{ t('notifications') }}</span>
        <span
          class="text-caption text-grey"
          style="cursor: pointer; text-decoration: underline"
          @click="handleClearAll"
          >Clear All</span
        >
      </v-card-title>
      <v-divider />
      <v-card-text class="pa-0" style="max-height: 400px; overflow-y: auto">
        <div v-if="sortedNotifications.length === 0" class="pa-4 text-center text-grey">
          {{ t('noData') }}
        </div>
        <v-list v-else density="compact" class="pa-0">
          <v-list-item
            v-for="notification in sortedNotifications"
            :key="notification.id"
            :class="{ 'notification-unread': !notification.read }"
            class="notification-item"
          >
            <template #prepend>
              <v-icon :color="getStatusColor(notification.status)" size="20">
                {{ getStatusIcon(notification.status) }}
              </v-icon>
            </template>

            <v-list-item-title class="d-flex flex-column ga-1">
              <div class="d-flex justify-space-between align-center">
                <span class="text-body-2 font-weight-medium text-truncate">
                  <template v-if="notification.title">{{ notification.title }}</template>
                  <template v-else>
                    {{ notification.status === 'success' ? 'Successful' : '' }}
                    {{ notification.status === 'error' ? 'Failed' : '' }}
                    {{ notification.status === 'uploading' ? 'Uploading...' : '' }}
                  </template>
                </span>
                <div class="d-flex align-center ga-2 flex-shrink-0">
                  <span class="text-caption text-grey">{{
                    utils.formatDateTime(notification.createdAt)
                  }}</span>
                </div>
              </div>

              <div class="w-100">
                <v-progress-linear
                  :model-value="notification.progress"
                  :color="getStatusColor(notification.status)"
                  height="2"
                />
                <div class="d-flex align-center justify-space-between">
                  <span class="text-caption text-grey"
                    >{{ Math.round(notification.progress) }}%</span
                  >
                  <span v-if="notification.message" class="text-caption text-grey">{{
                    notification.message
                  }}</span>
                  <span v-else class="text-caption text-grey"
                    >{{ notification.totalFiles ?? 0 }} files -
                    {{ utils.bytesToSize(notification.totalSize ?? 0) }}</span
                  >
                </div>
              </div>
            </v-list-item-title>
          </v-list-item>
        </v-list>
      </v-card-text>
    </v-card>
  </v-menu>
</template>

<style scoped>
.notification-item {
  border-bottom: 1px solid var(--sds-color-border-default-primary);
  padding: 12px;
}

.notification-item:last-child {
  border-bottom: none;
}

.notification-unread {
  background-color: var(--sds-color-background-surface-secondary);
}
</style>
